// src/services/clientService.js
// Centralized client/site query helpers.
// sites collection is the single source of truth (SOT) — client.sites[] embedded
// array is deprecated and must never be read/written by any new code.

import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase";

// Fetch clients that have at least one site in the sites collection.
// Used to filter "Select Client" dropdowns across Service Create, MRF, AMC Manual
// Entry, Machine Form, etc. — so operators can't accidentally pick a client that
// has no site to attach work to.
export async function fetchClientsWithSites(company_id) {
  const [clientSnap, siteSnap] = await Promise.all([
    getDocs(query(collection(db, "clients"), where("company_id", "==", company_id))),
    getDocs(query(collection(db, "sites"),   where("company_id", "==", company_id))),
  ]);

  const clientIdsWithSites = new Set(
    siteSnap.docs.map(d => d.data().client_id).filter(Boolean)
  );

  const allClients = clientSnap.docs.map(d => ({ id: d.id, ...d.data() }));
  return allClients.filter(c => clientIdsWithSites.has(c.id));
}

// Fetch all sites for a given client_id — always from the sites collection (SOT).
export async function fetchSitesForClient(company_id, client_id) {
  if (!client_id) return [];
  const snap = await getDocs(query(
    collection(db, "sites"),
    where("company_id", "==", company_id),
    where("client_id",  "==", client_id)
  ));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// Given an already-fetched sites array (from fetchSitesForClient), return the
// single site if there's exactly one — otherwise null. Callers fetch sites once
// for the dropdown and reuse that same array here — no extra Firestore call.
export function autoSelectSiteIfOnlyOne(sites) {
  if (sites.length === 1) {
    return sites[0];
  }
  return null;
}

// Fetch all projects linked to a given site_id — used for Costing Card Part A
// (Transport per-stop tagging, Expense project tagging) so cost-generating
// flows can attribute their financial_transactions entries to a project_id.
// NOTE: a site can have zero, one, or multiple projects against it — callers
// should auto-select when exactly one exists (same pattern as
// autoSelectSiteIfOnlyOne above) and show a picker otherwise.
export async function fetchProjectsForSite(company_id, site_id) {
  if (!site_id) return [];
  const snap = await getDocs(query(
    collection(db, "projects"),
    where("company_id", "==", company_id),
    where("site_id",    "==", site_id)
  ));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}
